import { useEffect, useState } from 'react';

import type { AuthenticationMethod } from 'modules/authentication-factor/models/authenticationFactors';

import { postPaymentPasswordProcedure } from '../api/postPaymentPasswordProcedure';

export const usePostPaymentPasswordProcedure = (
  {
    authenticationMethod,
    procedureId,
  }: {
    authenticationMethod: AuthenticationMethod;
    procedureId?: string;
  },
  callback?: (paymentPassword?: string) => void,
) => {
  const [procedureFailedReason, setProcedureFailedReason] = useState<
    'initializationError' | 'unknown' | 'timeout' | 'rejected' | undefined
  >(undefined);
  const [passwordToShow, setPasswordToShow] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    const handlePaymentPasswordGetting = async (interval?: NodeJS.Timeout) => {
      if (procedureId) {
        try {
          const response = await postPaymentPasswordProcedure({
            authenticationMethod,
            procedureId,
          });
          if (response.procedureStatus === 'approved') {
            setProcedureFailedReason(undefined);
            setPasswordToShow(response.paymentPassword);
            clearInterval(interval);
            callback?.(response.paymentPassword);
          }
          if (response.procedureStatus === 'rejected') {
            setProcedureFailedReason('rejected');
            clearInterval(interval);
            callback?.();
          }
          if (response.procedureStatus === 'timeout') {
            setProcedureFailedReason('timeout');
            clearInterval(interval);
            callback?.();
          }
        } catch {
          setProcedureFailedReason('unknown');
          callback?.();
        }
      }
    };

    if (procedureId) {
      // Using polling isn't ideal for the phone factor flow because we have
      // have the onMfaTokenValidated() callback at which point the related
      // procedure should already be approved.
      if (authenticationMethod === 'sca') {
        const interval = setInterval(
          () => handlePaymentPasswordGetting(interval),
          2500,
        );
        return () => clearInterval(interval);
      }

      handlePaymentPasswordGetting();
    }
  }, [procedureId]);

  return {
    procedureFailedReason,
    setProcedureFailedReason,
    passwordToShow,
  };
};
